// Lightweight shared store for saved/bookmarked listings (Increment 18).
import { useSyncExternalStore } from "react";
import { api, getToken } from "./api";

let revision = 0;
let keys = new Set<string>();
let loaded = false;
let loading: Promise<void> | null = null;
let pendingMutations = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const k = (kind: string, refId: string) => `${kind}:${refId}`;

export function ensureSavedLoaded() {
  if (loaded || loading) return loading ?? Promise.resolve();
  if (!getToken()) { loaded = true; return Promise.resolve(); }
  const startedRevision = revision;
  loading = api.savedKeys().then((list) => {
    if (startedRevision !== revision) return;
    keys = new Set(list); loaded = true; emit();
  }).catch(() => { if (startedRevision === revision) loaded = true; })
    .finally(() => { if (startedRevision === revision) loading = null; });
  return loading;
}
export function resetSaved() { revision++; keys = new Set(); loaded = false; loading = null; pendingMutations = 0; emit(); }

/** Set a reference deterministically; keep bookmark consumers in sync and rethrow after rollback. */
export async function setSaved(kind: string, refId: string, shouldSave: boolean): Promise<void> {
  const startedRevision = revision;
  const key = k(kind, refId);
  const wasSaved = keys.has(key);
  const next = new Set(keys);
  shouldSave ? next.add(key) : next.delete(key);
  pendingMutations++;
  keys = next; emit();
  try {
    if (shouldSave) await api.saveListing(kind, refId);
    else await api.unsaveListing(kind, refId);
  } catch (error: unknown) {
    if (startedRevision !== revision) throw error;
    const revert = new Set(keys);
    wasSaved ? revert.add(key) : revert.delete(key);
    keys = revert; emit();
    throw error;
  } finally {
    if (startedRevision === revision) { pendingMutations--; emit(); }
  }
}

export async function toggleSaved(kind: string, refId: string): Promise<boolean> {
  const shouldSave = !keys.has(k(kind, refId));
  // Preserve the existing toggle return contract; consumers observe rollback through the store.
  try { await setSaved(kind, refId, shouldSave); } catch { /* Store was rolled back. */ }
  return shouldSave;
}

function subscribe(cb: () => void) { listeners.add(cb); return () => listeners.delete(cb); }
export function useIsSaved(kind: string, refId: string): boolean {
  return useSyncExternalStore(subscribe, () => keys.has(k(kind, refId)), () => false);
}

/** Refresh must wait for bookmark writes so replacing rows never interrupts an active action. */
export function useSavedMutationPending(): boolean {
  return useSyncExternalStore(subscribe, () => pendingMutations > 0, () => false);
}
